// Shared by both forks.
import React, { useCallback, useContext, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
	spotlightAPI,
	getAPISpotlightCalls,
	handleSpotlightResults,
	handleSpotlightAssetsResponse,
} from '../api';
import { SpotlightSearchParams } from '../types';
import { isModule, isPublicOrShareCollectionView } from '../../../helpers/windowLocation';
import { InfoContext } from '../../../context';
import { resetStickySearchInAssetbank } from '../helpers/searchUtils';
import { SEARCH_DEBOUNCE_ms, SEARCH_MIN_CHARS } from '../constants';
import type { SelectedSearchOption } from '../helpers/types';
import usePrevious from '../../../helpers/usePrevious';
import { buildQueryWithSearchOptions } from '../SearchOptions/utils';
import { SEARCH_AND_OPERATOR_FEATURE_ID } from '../SearchOptions/constants';
import { usePortalFeatures } from '../../../helpers/usePortalFeatures';
import {
	readAppliedFilters,
	readAppliedFiltersForHybridSearch,
} from '../helpers/readAppliedFilters';
import { useLatestRequestSignal } from './useLatestRequestSignal';
import { useSetSpotlightInputFromExternalEvent } from './useSetSpotlightInputFromExternalEvent';
import { useSpotlightCapabilities } from './useSpotlightCapabilities';
import type { SearchAction } from '../SpotlightState';

type Options = {
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	dispatch: React.Dispatch<SearchAction>;
	collectionId: string | undefined;
	selectedSearchOptions: SelectedSearchOption[];
	isDropdownSearchDisabled: boolean;
	isApplyingSimilarityFilter: boolean;
	isHybridSearchOn: boolean;
	openSpotlightDropdown: () => void;
};

export function useSpotlightSearch({
	searchTerm,
	setSearchTerm,
	dispatch,
	collectionId,
	selectedSearchOptions,
	isDropdownSearchDisabled,
	isApplyingSimilarityFilter,
	isHybridSearchOn,
	openSpotlightDropdown,
}: Options) {
	const sharedCollectionId = window.bynder.propsAssetOverview?.collectionRecipientShareId;
	const isSharedCollection = isModule('share') || sharedCollectionId;
	const {
		general: { isLoggedIn },
	} = useContext(InfoContext);

	const previousSearchTerm = usePrevious(searchTerm);

	const { canShowStickySearch, canShowBrandguidelines } = useSpotlightCapabilities();

	const { features: portalFeatures, arePortalFeaturesResolved } = usePortalFeatures();
	const hasSearchAndOperatorFeature = portalFeatures.includes(SEARCH_AND_OPERATOR_FEATURE_ID);
	const isWaitingForSearchOperator =
		selectedSearchOptions.length > 0 && !arePortalFeaturesResolved;

	// Reflect in the input a "Did you mean…" corrected term published by paramount.
	useSetSpotlightInputFromExternalEvent(setSearchTerm);

	const { nextSignal, abort: abortSearchRequest } = useLatestRequestSignal();

	const searchDebounced = useDebouncedCallback(async (term: string) => {
		const isSearchingInsideACollection =
			collectionId || (isSharedCollection && sharedCollectionId);
		const searchOperatorToUse = hasSearchAndOperatorFeature ? 'AND' : 'OR';

		// Build search query based on selected search options, if any
		const searchQuery = buildQueryWithSearchOptions({
			selectedSearchOptions,
			searchTerm: term,
			searchOperator: searchOperatorToUse,
		});

		const { filters, searchTerms } = readAppliedFilters(searchOperatorToUse);

		// With only a shared collection id, this sends 'collection_undefined' and an empty collectionId (potential thing to check and fix or clean up)
		const params: SpotlightSearchParams = isSearchingInsideACollection
			? {
					filterKey: `collection_${collectionId}`,
					searchType: 'collection',
					collectionId: collectionId || '',
					...(sharedCollectionId && { recipientShareId: sharedCollectionId }),
			  }
			: {
					filters,
					...(searchTerms && !canShowStickySearch ? { searchTerms } : {}),
			  };

		const signal = nextSignal();

		try {
			let spotlightPromises;

			// A logged-out user's non-asset searches would only fail (401 / login redirect), so those results stay empty.
			const canSearchNonAssetEntities = isLoggedIn || !isPublicOrShareCollectionView();

			// If there are selected search options, we only search for assets (we don't search for other entities like collections etc)
			if (selectedSearchOptions.length === 0 && canSearchNonAssetEntities) {
				spotlightPromises = getAPISpotlightCalls(searchQuery, params, {
					canShowBrandguidelines,
					signal,
				});
			}

			// Only the ASSET spotlight results change: the other groups add on top of what the
			// asset bank has applied.
			const assetsParams =
				isHybridSearchOn && !isSearchingInsideACollection
					? readAppliedFiltersForHybridSearch(searchOperatorToUse)
					: params;

			const response = await spotlightAPI.searchSpotlightAssets(searchQuery, assetsParams, {
				useHybridSearch: isHybridSearchOn,
				signal,
			});

			if (signal.aborted) {
				return;
			}

			dispatch({ type: 'assetResultsReceived', ...handleSpotlightAssetsResponse(response) });

			if (spotlightPromises) {
				const responses = await Promise.all(spotlightPromises);

				if (signal.aborted) {
					return;
				}

				dispatch({
					type: 'groupResultsReceived',
					groups: handleSpotlightResults(responses),
				});
			}

			dispatch({ type: 'searchSucceeded', canShowBrandguidelines });
		} catch {
			// Not reported on purpose. Show "no results" rather than the previous term's results.
			if (!signal.aborted) {
				dispatch({ type: 'searchFailed' });
			}
		} finally {
			if (!signal.aborted) {
				dispatch({ type: 'searchSettled' });
			}
		}
	}, SEARCH_DEBOUNCE_ms);

	const cancelPendingSearch = () => {
		searchDebounced.cancel();
		abortSearchRequest();
	};

	// Keep `useCallback` here. The effect below that searches as the user types runs again whenever
	// `search` changes, and each run updates state, which renders the component again. If `search`
	// were a new function on every render, that effect would run on every render too, restarting the
	// fetch over and over.
	const search = useCallback(
		(term: string) => {
			if (term.length < SEARCH_MIN_CHARS) {
				return;
			}

			dispatch({ type: 'searchStarted' });
			searchDebounced(term);
		},
		[dispatch, searchDebounced],
	);

	const onValueChange = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
		const value = e?.currentTarget.value || '';
		const isEmptyTerm = value.length === 0;

		setSearchTerm(value);
		openSpotlightDropdown(); // If the user makes a search and closes the dropdown, when they type in the input, we re-open the dropdown to show the outcome of the new search

		if (isEmptyTerm || value.length < SEARCH_MIN_CHARS) {
			cancelPendingSearch();
			dispatch({ type: 'resultsDiscarded' });
		}

		if (previousSearchTerm && !value.length) {
			window.heap?.track('clear-spotlight-search-on-backspace');
		}

		if (canShowStickySearch && isEmptyTerm) {
			resetStickySearchInAssetbank(isHybridSearchOn, isApplyingSimilarityFilter);
		}
	};

	useEffect(() => {
		// Only search automatically when the dropdown searches at all, and not while a search-options
		// query is still waiting for the SearchAndOperator ECA to resolve (see
		// `isWaitingForSearchOperator`) — otherwise it would fire once with the default OR before the
		// operator is known.
		if (!isDropdownSearchDisabled && !isWaitingForSearchOperator) {
			search(searchTerm);
		}
		// `isHybridSearchOn` is in the deps so the re-fetch onto the other endpoint is explicit: none
		// of the other values change when the AI toggle flips on a PoC portal.
	}, [
		isHybridSearchOn,
		isDropdownSearchDisabled,
		isWaitingForSearchOperator,
		searchTerm,
		search,
		selectedSearchOptions,
	]);

	return { cancelPendingSearch, onValueChange };
}
